import { Component, OnInit, ViewEncapsulation, Input } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of, Observable } from 'rxjs';

import { AnalyticsService, ApplicationInsightsService } from '@crosscutting/public_api';

import { VMDataService, VeiligheidsMeterService, GraphQLVMService } from '../../../vm_api';
import { VMModule, VeiligheidsMeter, VMContentItem, VMModulesResponse, VMModulesContentResponse } from '../../../vm_models';
import { VMModulesMapper} from '../../../vm_mappers';

import { ContentHelper } from '../../../services/helpers/content-helper';
import { catchError, mergeMap, tap, map } from 'rxjs/operators';

@Component({
  selector: 'app-overzicht',
  templateUrl: './overzicht.component.html',
  styleUrls: ['./overzicht.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class OverzichtComponent implements OnInit {
  @Input() bekijkAria = '[not set]';
  @Input() bekijkText = '[not set]';
  @Input() modules: Array<any>;
  @Input() startAria = '[not set]';
  @Input() startText = '[not set]';
  @Input() vmReady = false;

  veiligheidsMeter: VeiligheidsMeter;
  vmContentItems: Array<VMContentItem>;
  pageTitle = '[not set]';
  pageContent = '[not set]';
  modulesTitle = '[not set]';
  paza = document.body.dataset.paza || '';
  pagename = document.body.dataset.pagename || '';

  constructor(
    private _vmDataService: VMDataService,
    private _analyticsService: AnalyticsService,
    private _applicationInsightsService: ApplicationInsightsService,
    private _router: Router,
    private graphQLVMService: GraphQLVMService,
    private veiligheidsMeterService: VeiligheidsMeterService,
  ) { }
  
  ngOnInit() {
    this.veiligheidsMeter = this._vmDataService.getVeiligheidsMeter();
    if (this.veiligheidsMeter == null) {
      this.initVeiligheidsMeter();
    } else {
      this.setPageContent();
      this.renderModules();
      this.vmReady = true;
    }

    // ivm timing issue setTimeout toegevoegd, omdat ga.min.js async ingeladen wordt
    setTimeout(() => {
      this._analyticsService.celebrusReinit();
      this._applicationInsightsService.logPageView(null, '/intro');
    }, 2000);
  }

  /* get module */
  getVMModuleContent(key) : Observable<VMModulesContentResponse> {
    return this.veiligheidsMeterService.getModuleVMContent(key)
      .pipe(tap(vmModuleContentResponse => {
        let module = new VMModule;

        module = VMModulesMapper.mapContent(vmModuleContentResponse);

        if (module.key === key) {
          let i = this.veiligheidsMeter.vmModules.findIndex((x) => x.key == key);
          module.status = '';
          this.veiligheidsMeter.vmModules[i] = module;
        }
        this._vmDataService.saveVeiligheidsMeter(this.veiligheidsMeter);
      }, () => {
        this.dataError();
      }));
  }

  /* Go to the selected module */
  goToModule(id) {
    let selectedModule = this.veiligheidsMeter.vmModules.find(x => x.key == id);
    
    if (!selectedModule?.vragen) {
      this.getVMModuleContent(selectedModule?.key);
    }
    setTimeout(() => {
      this.veiligheidsMeter.currentVMModule = id;
      this._vmDataService.saveVeiligheidsMeter(this.veiligheidsMeter);
      this._router.navigate(['/check']);
    });
  }
  
  dataError() {
    console.error('Error while fetching content');
    this._router.navigate(['/error']);
  }

  getAvailableModules() : Observable<VMModulesResponse> {
    return this.veiligheidsMeterService.getAvailableVMModules().pipe(tap(vmModulesResponse => 
      {
        if (vmModulesResponse === null) {
          this.dataError();
        }
        this.veiligheidsMeter.vmModules = vmModulesResponse.modules;
        this.veiligheidsMeter.vmModuleKeys = VMModulesMapper.mapKeys(vmModulesResponse);
      }));
  }

  /* Fill the veiligheidsmeter with the modules and connected content */
  initVeiligheidsMeter() {
    let observables = [];
    this.veiligheidsMeter = new VeiligheidsMeter();
    this.veiligheidsMeter.vmModules = new Array<VMModule>;

    forkJoin({
      items: this.graphQLVMService.getContentItems(ContentHelper.getContentItemsPath()),
      tips: this.graphQLVMService.getContentItemsTips(ContentHelper.getTipsPath()),
      prefetch: this.veiligheidsMeterService.prefetch(),
      available: this.getAvailableModules()
    }).subscribe(result => {
      this.veiligheidsMeter.vmContentItems = result.items.concat(result.tips);
      this.veiligheidsMeter.vmModuleKeys.forEach((key) => {
        observables.push(this.getVMModuleContent(key));
      });
        
      forkJoin(observables).subscribe(() => {
        this.renderModules();
        this.veiligheidsMeter.vmReady = true;
        this._vmDataService.saveVeiligheidsMeter(this.veiligheidsMeter);
        this.setPageContent();
        this.vmReady = true;
      });
    }, () => this.dataError());
  }

  /* fetch and store the modules */
  renderModules() {
    let modules = [];

    this.veiligheidsMeter.vmModules.forEach((module) => {
      let closed = module.status !== "" && module.status !== undefined,
        meetid = closed ? 'bekijk' : 'start',
        badgeUrl = closed ? '/assets/images/' + module.status + '.png' : '/assets/images/placeholder.png',
        badgeAlt = closed ? this.setBadgeAlt(module.status) : this.setBadgeAlt('placeholder'),
        vmModule = {
          status: closed,
          key: module.key,
          name: module.naam,
          meetid: meetid,
          badgeUrl: badgeUrl,
          badgeAlt: badgeAlt
        }
      modules.push(vmModule);
    })
    this.modules = modules;
  }

  setBadgeAlt(status) {
    let badgeCI = ContentHelper.getBadgeAlt(),
        contentItem,
        badgeAltText = 'badge';

    try {
      contentItem = this.veiligheidsMeter.vmContentItems.find(x => x.id === badgeCI).content;
      badgeAltText = JSON.parse(contentItem)[status];
    } catch {
      console.error('No alt found for', contentItem);
    }
    return badgeAltText;
  }

  /* Set the page content */
  setPageContent() {
    let content = ContentHelper.getContent('overzicht');

    Object.keys(content).forEach((key) => {
      let item = Object.keys(content[key])[0];

      if (this[key]) {
        let contentItem;
        
        try { 
          contentItem = this.veiligheidsMeter.vmContentItems.find(x => x.id === content[key][item])[item];
          this[key] = contentItem;
        } catch {} 
      } else {
        console.error(key, ' key not found on component');
      }
    });
  }
}

